import { updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { viewUploadedFile } from '../../../utils';

export const UploadSignedBylaws = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;
  const { setNotification } = useNotification();

  const uploadDocumentModalVisibility = useToggle();
  const [file, setFile] = useState<FileBasic | null>(null);

  const latestSignedBylawsFileId = (() => {
    const docs = serviceRequest?.corporation?.corporationDocuments || [];
    const signedDoc =
      docs.find((d) => d?.documentType === LegacyDocumentType.Bylaws) ||
      docs.find((d) => d?.documentType === LegacyDocumentType.BylawsUnsigned);
    const fl = signedDoc?.files?.sort((a, b) => b.id - a.id)?.[0];
    return fl?.id;
  })();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex items-center gap-2">
                {latestSignedBylawsFileId ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => viewUploadedFile(latestSignedBylawsFileId)}
                  >
                    <Icons.Eye className="size-4 mr-2" />
                    View Signed Bylaws
                  </Button>
                ) : null}
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Signed bylaws uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadDocumentModalVisibility.show}
                variant="primary"
              >
                <Icons.Upload01 className="size-4 mr-2" />
                Upload Signed Bylaws
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload signed bylaws" />,
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4 mr-2" />
                Upload Signed Bylaws
              </Button>
            ),
            label: <ServiceRequestTaskLabel label="Upload signed bylaws" />,
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: latestSignedBylawsFileId ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => viewUploadedFile(latestSignedBylawsFileId)}
              >
                <Icons.Eye className="size-4 mr-2" />
                View signed bylaws
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="Client uploaded signed bylaws"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Waiting for client to upload signed bylaws" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Waiting for client to upload signed bylaws" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadDocumentModalVisibility.visible}
        setIsOpen={uploadDocumentModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload Signed Bylaws"
        documentType={LegacyDocumentType.Bylaws}
        onUploadSuccess={async () => {
          try {
            const updateServiceRequestRes = await updateTask({
              id: task.id,
              data: { status: TaskStatus.COMPLETED },
            });
            if (updateServiceRequestRes?.ok) {
              await mutate();
              setNotification({
                show: true,
                valid: true,
                title: 'Success',
                message: 'Signed bylaws uploaded successfully',
              });
            }
          } catch (error) {
            console.error('Failed to update task:', error);
            setNotification({
              show: true,
              valid: false,
              title: 'Error',
              message: 'Failed to upload signed bylaws',
            });
          }
        }}
      />
    </div>
  );
};
